import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { DashboardSkeleton } from '../components/common/Skeleton';
import CertificateCard from '../components/common/CertificateCard';
import FirstReportCard from '../components/dashboard/FirstReportCard';
import { readOnboardingState } from '../utils/onboarding';
import { reportOwnerOpenUrl } from '../utils/site';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const hasMeaningfulReport = (report) => {
  if (report?.status === 'signed') return true;
  const body = String(report?.content || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();
  return Boolean(body || String(report?.patient_name || '').trim());
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshProfile } = useAuth();
  const { reports, stats, loading: dataLoading } = useData();

  useEffect(() => {
    if (user) refreshProfile();
  }, []);

  if (authLoading || dataLoading || !user) {
    return <div className="max-w-7xl mx-auto px-4 py-8"><DashboardSkeleton /></div>;
  }

  const onboarding = readOnboardingState(typeof localStorage !== 'undefined' ? localStorage : null, user.id);
  const hasMeaningfulReportInAccount = reports.some(hasMeaningfulReport);
  const showActivationCard = !hasMeaningfulReportInAccount && onboarding.status !== 'activated';
  const signedCount = reports.filter(r => r.status === 'signed').length;
  const draftCount = reports.filter(r => r.status === 'draft').length;
  const usagePercentage = stats?.limit > 0 ? (stats.used / stats.limit) * 100 : 0;
  const overLimit = usagePercentage > 90;
  const tier = user.plan_tier?.toLowerCase() || 'free';
  const isTopTier = tier === 'pro' || tier === 'profissional';

  return (
    <div className="max-w-7xl mx-auto px-4 py-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
        <div>
          <h1 className="font-display text-[32px] font-bold text-navy tracking-tight leading-none mb-2">Olá, {user.name.split(' ')[0]}</h1>
          <p className="text-sm text-muted capitalize">{format(new Date(), "EEEE, d 'de' MMMM", { locale: ptBR })}</p>
        </div>

        <div className="bg-paper rounded-card border border-line p-4 shadow-card flex items-center gap-4 min-w-[280px]">
          <div className="flex-1">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-semibold text-muted uppercase tracking-widest">Plano {user.plan_tier || 'Free'}</span>
              <Link to="/planos" className="text-[10px] font-semibold text-navy uppercase tracking-widest hover:underline">{isTopTier ? 'Gerenciar' : 'Fazer upgrade'}</Link>
            </div>
            <div className="w-full h-1.5 bg-paper-alt rounded-full overflow-hidden"><div className={`h-full transition-all duration-500 ${overLimit ? 'bg-danger' : 'bg-navy'}`} style={{ width: `${Math.min(usagePercentage, 100)}%` }} /></div>
            <p className="text-xs text-muted mt-1.5 tabular-nums">{stats?.used} de {stats?.limit} laudos este mês</p>
          </div>
        </div>
      </div>

      {showActivationCard && <FirstReportCard />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <div className="bg-paper rounded-card border border-line p-8 shadow-card"><p className="text-[36px] font-bold text-ink leading-none mb-2 tabular-nums">{reports.length}</p><p className="text-xs font-semibold text-muted uppercase tracking-widest">Total de laudos</p></div>
        <div className="bg-paper rounded-card border border-line p-8 shadow-card"><p className="text-[36px] font-bold text-ink leading-none mb-2 tabular-nums">{signedCount}</p><p className="text-xs font-semibold text-muted uppercase tracking-widest">Registrados</p></div>
        <div className="bg-paper rounded-card border border-line p-8 shadow-card"><p className="text-[36px] font-bold text-ink leading-none mb-2 tabular-nums">{draftCount}</p><p className="text-xs font-semibold text-muted uppercase tracking-widest">Rascunhos</p></div>
        <Link to="/novo-laudo" className="bg-selo rounded-card p-8 flex flex-col justify-center hover:bg-selo-dark transition-colors group"><span className="text-lg font-semibold text-white">Novo laudo</span></Link>
      </div>

      <div>
        <div className="flex items-center justify-between mb-8"><h2 className="font-display text-xl font-bold text-navy tracking-tight">Atividade recente</h2><Link to="/meus-laudos" className="flex items-center gap-2 text-sm font-semibold text-navy hover:underline transition-colors">Ver todo o histórico <ArrowUpRight className="w-4 h-4" /></Link></div>

        {reports.length === 0 ? (
          <div className="bg-paper rounded-card border border-line shadow-card flex flex-col items-center justify-center py-20 text-center px-4">
            <p className="text-sm font-semibold text-ink mb-1">Nenhum laudo ainda</p>
            <p className="text-sm text-muted mb-6 max-w-xs">Seu primeiro laudo leva poucos minutos: escolha um modelo, preencha e registre.</p>
            <Link to="/novo-laudo" className="btn-convert">Criar meu primeiro laudo</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {reports.slice(0, 6).map((report) => (
              <button key={report.id} onClick={() => {
                if (report.status === 'signed') window.open(reportOwnerOpenUrl(report.id, report.access_token), '_blank', 'noopener');
                else navigate(`/editar-laudo/${report.id}`);
              }} className="text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-navy rounded-card">
                <CertificateCard variant="compact" className="hover:border-navy/40 transition-colors h-full" title={`Laudo #${report.id.slice(0, 8).toUpperCase()}`} patient={report.patient_name || 'Paciente sem nome'} date={report.exam_date || format(new Date(report.created_at), 'dd/MM/yyyy')} hash={report.hash} status={report.status === 'signed' ? 'registered' : 'draft'} />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
